import { DOCUMENT, Injectable, inject } from '@angular/core';

import { IZipEntry, zipStore } from '../models/zip.model';

/* -------------------------------------------------------------------------- */
/*                               ANCHOR modelos                               */
/* -------------------------------------------------------------------------- */

/** Una columna del archivo exportado. */
export interface IExportColumn {
  /** Clave dentro del objeto de fila. */
  key: string;
  title: string;
  /**
   * Unidad del valor (`%`). Va al encabezado, **no a la celda**: «72» es un
   * número con el que se puede calcular y «72 %» es texto.
   */
  suffix?: string;
}

/** Un bloque de datos: una tabla con su título. */
export interface IExportBlock {
  titulo: string;
  columnas: Array<IExportColumn>;
  filas: Array<any>;
}

/** Encabezado del archivo: qué se está exportando y con qué filtros. */
export interface IExportMeta {
  /** Sección o pestaña de la que salen los datos. */
  titulo: string;
  periodo?: string;
  /** Filtros aplicados, ya legibles. */
  filtros?: string;
}

/** Separador de columnas del CSV. Coma, que es el estándar del formato. */
const SEP = ',';

const SALTO = '\r\n';

/**
 * Excel decide la codificación de un CSV por el BOM. Sin él, «Región» llega
 * como «RegiÃ³n».
 */
const BOM = '﻿';

/* -------------------------------------------------------------------------- */
/*                               ANCHOR servicio                              */
/* -------------------------------------------------------------------------- */

/**
 * Exportación de lo que se está viendo.
 *
 * Todos los botones del tablero pasan por aquí, y todos trabajan sobre los
 * datos **ya filtrados y derivados** que la pantalla tiene en la mano: lo que
 * baja es lo que se ve, no la constante de `data/`.
 *
 * Reparto de formatos:
 *
 * | Botón   | Qué produce |
 * |---------|-------------|
 * | CSV     | `.csv` con BOM UTF-8 — estándar, lo abre cualquier cosa |
 * | Excel   | `.xlsx` de verdad, con los números tipados como números |
 * | Copiar  | portapapeles en TSV, que es lo que pega bien en una hoja |
 * | PDF     | vista de impresión aislada; el navegador guarda como PDF |
 * | Print   | la misma vista, pensada para papel |
 * | IMAGEN  | PNG del lienzo, que ECharts sabe entregar solo |
 *
 * El CSV admite varios bloques en un mismo archivo, separados por una línea en
 * blanco: una sección del tablero no es una tabla, son ocho, y repartirlas en
 * ocho descargas sería peor que un archivo con ocho apartados.
 */
@Injectable({ providedIn: 'root' })
export class ExportService {

  private readonly document = inject(DOCUMENT);

  /* -------------------------------------------------------------------------- */
  /*                                 ANCHOR CSV                                 */
  /* -------------------------------------------------------------------------- */

  public csv(bloques: Array<IExportBlock>, archivo: string, meta?: IExportMeta): void {
    const partes: Array<string> = [];

    if (meta) {
      partes.push(this.encabezado(meta), '');
    }

    for (const bloque of bloques) {
      if (!bloque.filas.length) {
        partes.push(this.escapar(bloque.titulo), 'Sin registros para los filtros aplicados.', '');

        continue;
      }

      partes.push(this.escapar(bloque.titulo));
      partes.push(bloque.columnas.map((columna) => this.escapar(this.titulo(columna))).join(SEP));

      for (const fila of bloque.filas) {
        partes.push(bloque.columnas
          .map((columna) => this.escapar(this.valor(fila[columna.key])))
          .join(SEP));
      }

      partes.push('');
    }

    this.descargar(
      new Blob([BOM + partes.join(SALTO)], { type: 'text/csv;charset=utf-8' }),
      `${archivo}.csv`,
    );
  }

  /** Bloque de contexto del archivo: sin esto, un CSV suelto no dice de qué es. */
  private encabezado(meta: IExportMeta): string {
    const lineas: Array<Array<string>> = [
      ['Zorro Abarrotero · Programa NPS'],
      ['Sección', meta.titulo],
    ];

    if (meta.periodo) {
      lineas.push(['Periodo', meta.periodo]);
    }

    lineas.push(['Filtros', meta.filtros || 'Sin filtros']);
    lineas.push(['Generado', this.marcaDeTiempo()]);

    return lineas
      .map((celdas) => celdas.map((celda) => this.escapar(celda)).join(SEP))
      .join(SALTO);
  }

  /**
   * Comillas sólo cuando hacen falta (separador, comillas o salto de línea) y
   * las comillas internas se duplican, que es como manda el RFC 4180.
   */
  private escapar(valor: string): string {
    return /["\n\r,;]/.test(valor) ? `"${valor.replace(/"/g, '""')}"` : valor;
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR XLSX                                 */
  /* -------------------------------------------------------------------------- */

  /** Cada bloque es una hoja del libro. */
  public xlsx(bloques: Array<IExportBlock>, archivo: string): void {
    const hojas = bloques.length ? bloques : [{ titulo: 'Hoja1', columnas: [], filas: [] }];
    const nombres = this.nombresDeHoja(hojas.map((hoja) => hoja.titulo));

    const entradas: Array<IZipEntry> = [
      { ruta: '[Content_Types].xml', contenido: this.contentTypes(hojas.length) },
      { ruta: '_rels/.rels', contenido: this.relsRaiz() },
      { ruta: 'xl/workbook.xml', contenido: this.workbook(nombres) },
      { ruta: 'xl/_rels/workbook.xml.rels', contenido: this.relsLibro(hojas.length) },
      ...hojas.map((hoja, i) => ({
        ruta: `xl/worksheets/sheet${i + 1}.xml`,
        contenido: this.hoja(hoja),
      })),
    ];

    this.descargar(zipStore(entradas), `${archivo}.xlsx`);
  }

  /**
   * Excel rechaza el libro si dos hojas se llaman igual, si el nombre pasa de
   * 31 caracteres o si trae `[]:*?/\`. Los títulos de los paneles hacen las
   * tres cosas, así que se saneen y se desempatan.
   */
  private nombresDeHoja(titulos: Array<string>): Array<string> {
    const usados = new Set<string>();

    return titulos.map((titulo, i) => {
      const limpio = (titulo.replace(/[[\]:*?/\\]/g, ' ').trim() || `Hoja${i + 1}`).slice(0, 31);
      let nombre = limpio;
      let n = 2;

      while (usados.has(nombre.toLowerCase())) {
        const sufijo = ` (${n++})`;
        nombre = limpio.slice(0, 31 - sufijo.length) + sufijo;
      }

      usados.add(nombre.toLowerCase());

      return nombre;
    });
  }

  private hoja(bloque: IExportBlock): string {
    const filas: Array<string> = [];

    filas.push(this.filaXml(1, bloque.columnas.map((columna) => this.titulo(columna))));

    bloque.filas.forEach((fila, i) => {
      filas.push(this.filaXml(
        i + 2,
        bloque.columnas.map((columna) => fila[columna.key]),
      ));
    });

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
      + `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
      + `<sheetData>${filas.join('')}</sheetData>`
      + `</worksheet>`;
  }

  private filaXml(numero: number, valores: Array<any>): string {
    const celdas = valores
      .map((valor, i) => this.celdaXml(`${this.columnaExcel(i)}${numero}`, valor))
      .join('');

    return `<row r="${numero}">${celdas}</row>`;
  }

  /**
   * Un número se escribe como número —así se puede sumar en la hoja— y todo lo
   * demás como texto en línea, para no tener que mantener `sharedStrings.xml`.
   */
  private celdaXml(ref: string, valor: any): string {
    if (typeof valor === 'number' && Number.isFinite(valor)) {
      return `<c r="${ref}"><v>${valor}</v></c>`;
    }

    const texto = this.valor(valor);

    if (!texto) {
      return `<c r="${ref}"/>`;
    }

    return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${this.xml(texto)}</t></is></c>`;
  }

  /** 0 → A, 25 → Z, 26 → AA. */
  private columnaExcel(indice: number): string {
    let letras = '';
    let n = indice;

    do {
      letras = String.fromCharCode(65 + (n % 26)) + letras;
      n = Math.floor(n / 26) - 1;
    } while (n >= 0);

    return letras;
  }

  private xml(texto: string): string {
    return texto
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Los caracteres de control no son válidos en XML 1.0 y tumban el libro.
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
  }

  private contentTypes(hojas: number): string {
    const overrides = Array.from({ length: hojas }, (_, i) =>
      `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
    ).join('');

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
      + `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
      + `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>`
      + `<Default Extension="xml" ContentType="application/xml"/>`
      + `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>`
      + overrides
      + `</Types>`;
  }

  private relsRaiz(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
      + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
      + `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>`
      + `</Relationships>`;
  }

  private workbook(nombres: Array<string>): string {
    const hojas = nombres
      .map((nombre, i) => `<sheet name="${this.xml(nombre)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
      .join('');

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
      + `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"`
      + ` xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
      + `<sheets>${hojas}</sheets>`
      + `</workbook>`;
  }

  private relsLibro(hojas: number): string {
    const relaciones = Array.from({ length: hojas }, (_, i) =>
      `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
    ).join('');

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
      + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
      + relaciones
      + `</Relationships>`;
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR PORTAPAPELES                           */
  /* -------------------------------------------------------------------------- */

  /**
   * Copia en TSV: pegar en Excel o en Sheets reparte las columnas solo, cosa
   * que con comas no pasa.
   */
  public async copiar(bloques: Array<IExportBlock>): Promise<boolean> {
    const texto = bloques
      .map((bloque) => [
        bloque.titulo,
        bloque.columnas.map((columna) => this.titulo(columna)).join('\t'),
        ...bloque.filas.map((fila) => bloque.columnas
          // Una tabulación dentro de una celda partiría la fila en dos columnas.
          .map((columna) => this.valor(fila[columna.key]).replace(/\t/g, ' '))
          .join('\t')),
      ].join('\n'))
      .join('\n\n');

    try {
      await navigator.clipboard.writeText(texto);

      return true;
    } catch {
      // Safari sin permiso, contexto no seguro o el usuario lo denegó.
      return false;
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR IMPRESIÓN                              */
  /* -------------------------------------------------------------------------- */

  /**
   * Abre el diálogo de impresión con **sólo** estos bloques.
   *
   * Se pinta en un `iframe` fuera de pantalla en vez de imprimir la página:
   * `window.print()` a secas mandaría a la impresora el panel lateral, la barra
   * superior y las gráficas de cristal, que en papel son manchas grises. Y un
   * `window.open()` lo bloquearían los bloqueadores de ventanas emergentes.
   *
   * Es también lo que hay detrás del botón «PDF»: el propio navegador ofrece
   * «Guardar como PDF» en ese diálogo, y el título del documento se usa como
   * nombre del archivo. Generar el PDF sin ayuda del navegador pedía una
   * dependencia que este proyecto no tiene.
   */
  public imprimir(bloques: Array<IExportBlock>, meta: IExportMeta): void {
    const marco = this.document.createElement('iframe');

    marco.setAttribute('aria-hidden', 'true');
    marco.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    this.document.body.appendChild(marco);

    const ventana = marco.contentWindow;
    const documento = marco.contentDocument;

    if (!ventana || !documento) {
      marco.remove();

      return;
    }

    documento.open();
    documento.write(this.paginaImprimible(bloques, meta));
    documento.close();

    // El diálogo bloquea el hilo; al volver ya se puede retirar el marco.
    const lanzar = (): void => {
      ventana.focus();
      ventana.print();
      setTimeout(() => marco.remove(), 500);
    };

    if (documento.readyState === 'complete') {
      lanzar();
    } else {
      ventana.addEventListener('load', lanzar, { once: true });
    }
  }

  /**
   * Hoja en blanco y negro. No hereda nada del tablero a propósito: el
   * glassmorphism vive de transparencias y en papel no se lee.
   */
  private paginaImprimible(bloques: Array<IExportBlock>, meta: IExportMeta): string {
    const contexto = [
      meta.periodo ? `Periodo: ${meta.periodo}` : '',
      `Filtros: ${meta.filtros || 'sin filtros'}`,
      `Generado: ${this.marcaDeTiempo()}`,
    ].filter(Boolean).map((linea) => `<p class="meta">${this.xml(linea)}</p>`).join('');

    const tablas = bloques.map((bloque) => {
      if (!bloque.filas.length) {
        return `<h2>${this.xml(bloque.titulo)}</h2>`
          + `<p class="vacio">Sin registros para los filtros aplicados.</p>`;
      }

      const encabezados = bloque.columnas
        .map((columna) => `<th>${this.xml(this.titulo(columna))}</th>`)
        .join('');
      const filas = bloque.filas.map((fila) => {
        const celdas = bloque.columnas
          .map((columna) => {
            const valor = fila[columna.key];
            const clase = typeof valor === 'number' ? ' class="num"' : '';

            return `<td${clase}>${this.xml(this.valor(valor))}</td>`;
          })
          .join('');

        return `<tr>${celdas}</tr>`;
      }).join('');

      return `<h2>${this.xml(bloque.titulo)}</h2>`
        + `<table><thead><tr>${encabezados}</tr></thead><tbody>${filas}</tbody></table>`;
    }).join('');

    return `<!doctype html><html lang="es-MX"><head><meta charset="utf-8">`
      + `<title>${this.xml(meta.titulo)}</title><style>`
      + `@page { margin: 14mm; size: landscape; }`
      + `* { box-sizing: border-box; }`
      + `body { color: #111; font: 11px/1.45 -apple-system, "Segoe UI", Arial, sans-serif; margin: 0; }`
      + `h1 { font-size: 19px; margin: 0 0 2px; }`
      + `h2 { border-bottom: 1px solid #999; font-size: 13px; margin: 20px 0 6px; padding-bottom: 3px; }`
      + `.meta { color: #555; font-size: 10px; margin: 1px 0; }`
      + `.vacio { color: #555; font-style: italic; }`
      + `table { border-collapse: collapse; page-break-inside: auto; width: 100%; }`
      + `th, td { border: 1px solid #bbb; padding: 4px 6px; text-align: left; }`
      + `th { background: #eee; font-weight: 700; }`
      + `td.num { text-align: right; }`
      + `tr { page-break-inside: avoid; }`
      // Sin esto, una tabla que cruza de página pierde sus encabezados.
      + `thead { display: table-header-group; }`
      + `</style></head><body>`
      + `<h1>${this.xml(meta.titulo)}</h1>${contexto}${tablas}`
      + `</body></html>`;
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR ARCHIVOS                              */
  /* -------------------------------------------------------------------------- */

  /** Guarda un PNG que ECharts ya entregó como `data:` URL. */
  public png(dataUrl: string, archivo: string): void {
    this.enlazarYPulsar(dataUrl, `${archivo}.png`);
  }

  public descargar(blob: Blob, archivo: string): void {
    const url = URL.createObjectURL(blob);

    this.enlazarYPulsar(url, archivo);
    // Revocar en el acto cancelaría la descarga en Firefox.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  private enlazarYPulsar(url: string, archivo: string): void {
    const enlace = this.document.createElement('a');

    enlace.download = archivo;
    enlace.href = url;
    enlace.rel = 'noopener';
    enlace.style.display = 'none';

    this.document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR AUXILIARES                            */
  /* -------------------------------------------------------------------------- */

  /** `nps-general (%)`: la unidad va en el encabezado, no en cada celda. */
  private titulo(columna: IExportColumn): string {
    const unidad = columna.suffix?.trim();

    return unidad ? `${columna.title} (${unidad})` : columna.title;
  }

  private valor(valor: any): string {
    return valor === null || valor === undefined ? '' : String(valor);
  }

  private marcaDeTiempo(): string {
    return new Date().toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  }

  /**
   * Nombre de archivo sin acentos ni espacios: un `Voz del cliente · Año.csv`
   * llega con caracteres cambiados a según qué sistema de archivos.
   */
  public nombreArchivo(...partes: Array<string>): string {
    const base = partes
      .filter(Boolean)
      .join('-')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const hoy = new Date();
    const fecha = `${hoy.getFullYear()}${`${hoy.getMonth() + 1}`.padStart(2, '0')}${`${hoy.getDate()}`.padStart(2, '0')}`;

    return `${base || 'exportacion'}-${fecha}`;
  }
}
